import React, { useEffect, useState, useRef } from "react";
import { Plus, Trash2, Save, Upload, Loader2, FileText, X, Clock, CheckCircle2, Download, GripVertical, EyeOff } from "lucide-react";
import { adminApi, mediaUrl } from "../../lib/api";
import ImageUpload from "./ImageUpload";
import { toast } from "sonner";

const emptyGuide = { title: "", description: "", file_url: "", image: "", image_alt: "", active: true, order: 0, followup_enabled: false, followup_subject: "", followup_body: "", followup_cta_label: "", followup_cta_link: "" };

export default function GuidesManager() {
  const [guides, setGuides] = useState([]);
  const [leads, setLeads] = useState([]);
  const [editing, setEditing] = useState(null); // guide object or null
  const [loading, setLoading] = useState(true);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const load = () => {
    adminApi.guides.list().then(setGuides).catch(() => setGuides([]));
    adminApi.guideLeads.list().then(setLeads).catch(() => setLeads([]));
  };
  useEffect(() => { load(); setLoading(false); }, []);

  const startNew = () => setEditing({ ...emptyGuide, order: guides.length + 1 });
  const remove = async (id) => {
    if (!window.confirm("Delete this guide?")) return;
    await adminApi.guides.remove(id);
    toast.success("Guide deleted");
    load();
  };

  const toggleActive = async (g) => {
    const next = g.active === false;
    setGuides((gs) => gs.map((x) => (x.id === g.id ? { ...x, active: next } : x)));
    try { await adminApi.guides.update(g.id, { ...g, active: next }); toast.success(next ? "Guide shown on site" : "Guide hidden"); }
    catch { toast.error("Couldn't update"); load(); }
  };

  const handleDrop = async (dropIndex) => {
    setOverIndex(null);
    if (dragIndex === null || dragIndex === dropIndex) { setDragIndex(null); return; }
    const reordered = [...guides];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    setGuides(reordered);
    setDragIndex(null);
    try { await adminApi.guidesReorder(reordered.map((i) => i.id)); toast.success("Order updated"); load(); }
    catch { toast.error("Couldn't save order"); load(); }
  };

  const exportLeads = () => {
    const rows = [["Name", "Email", "Guide", "Date"], ...leads.map((l) => [l.name, l.email, l.guide_title, l.created_at])];
    const csv = rows.map((r) => r.map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "guide-downloads.csv";
    a.click();
  };

  if (editing) return <GuideEditor guide={editing} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display font-extrabold text-2xl text-navy">Downloadable Guides</h1>
        <button data-testid="guide-add-btn" onClick={startNew} className="inline-flex items-center gap-1.5 bg-orange hover:bg-orange-dark text-white font-bold text-sm px-5 py-2.5 rounded-full transition-colors"><Plus size={16} /> Add guide</button>
      </div>
      <p className="text-slate-500 text-sm mb-8">Manage the free guides shown on the home page. A guide shows "Coming Soon" until you upload its document. When a visitor downloads a guide, we capture their name and email below. <span className="inline-flex items-center gap-1 text-slate-400"><GripVertical size={13} /> Drag rows to reorder.</span></p>

      <div className="space-y-3 mb-12">
        {guides.length === 0 && !loading && <p className="text-sm text-slate-400">No guides yet.</p>}
        {guides.map((g, index) => (
          <div key={g.id} data-testid={`guide-row-${g.id}`}
            onDragOver={(e) => { e.preventDefault(); setOverIndex(index); }}
            onDrop={() => handleDrop(index)}
            onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
            className={`flex items-center gap-4 bg-white rounded-2xl border p-4 shadow-sm transition-all ${overIndex === index && dragIndex !== null ? "border-orange ring-2 ring-orange/30" : "border-slate-100"} ${dragIndex === index ? "opacity-50" : ""}`}>
            <button draggable onDragStart={() => setDragIndex(index)} className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-navy shrink-0 touch-none" title="Drag to reorder" data-testid={`guide-drag-${g.id}`}><GripVertical size={16} /></button>
            <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${g.file_url ? "bg-orange/10 text-orange" : "bg-slate-100 text-slate-400"}`}>
              {g.file_url ? <FileText size={20} /> : <Clock size={20} />}
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-navy truncate">{g.title || "Untitled guide"}</p>
              <p className="text-xs mt-0.5 flex items-center gap-1.5">
                {g.file_url
                  ? <span className="text-green-600 font-semibold inline-flex items-center gap-1"><CheckCircle2 size={13} /> Document uploaded</span>
                  : <span className="text-amber-500 font-semibold inline-flex items-center gap-1"><Clock size={13} /> Coming soon (no document)</span>}
                <span className="text-slate-300">•</span><span className="text-slate-400">Order {g.order}</span>
                <span className="text-slate-300">•</span><span className="text-navy font-semibold inline-flex items-center gap-1"><Download size={12} /> {leads.filter((l) => l.guide_id === g.id).length} downloads</span>
                {g.active === false && <><span className="text-slate-300">•</span><span className="text-slate-400 font-semibold inline-flex items-center gap-1"><EyeOff size={12} /> Hidden</span></>}
              </p>
            </div>
            <button
              data-testid={`guide-toggle-active-${g.id}`}
              onClick={() => toggleActive(g)}
              title={g.active === false ? "Hidden — click to show on site" : "Visible — click to hide"}
              className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${g.active === false ? "bg-slate-200" : "bg-orange"}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${g.active === false ? "" : "translate-x-5"}`} />
            </button>
            <button onClick={() => setEditing(g)} className="text-sm font-semibold text-orange hover:text-orange-dark px-3 py-1.5">Edit</button>
            <button data-testid={`guide-delete-${g.id}`} onClick={() => remove(g.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-bold text-xl text-navy">Captured Downloads <span className="text-slate-400 font-normal text-base">({leads.length})</span></h2>
        {leads.length > 0 && <button data-testid="guide-leads-export" onClick={exportLeads} className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-orange"><Download size={15} /> Export CSV</button>}
      </div>
      {leads.length === 0 ? (
        <p className="text-sm text-slate-400">No downloads captured yet.</p>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr><th className="px-4 py-3 font-semibold">Name</th><th className="px-4 py-3 font-semibold">Email</th><th className="px-4 py-3 font-semibold">Guide</th><th className="px-4 py-3 font-semibold">Date</th></tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 text-navy font-medium">{l.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{l.email}</td>
                  <td className="px-4 py-3 text-slate-600">{l.guide_title}</td>
                  <td className="px-4 py-3 text-slate-400">{(l.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function GuideEditor({ guide, onDone, onCancel }) {
  const [form, setForm] = useState({ ...emptyGuide, ...guide });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await adminApi.uploadDocument(fd);
      set("file_url", res.url);
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (guide.id) await adminApi.guides.update(guide.id, form);
      else await adminApi.guides.create(form);
      toast.success("Guide saved");
      onDone();
    } catch { toast.error("Could not save"); setSaving(false); }
  };

  return (
    <div>
      <button onClick={onCancel} className="text-sm font-semibold text-slate-500 hover:text-navy mb-4 inline-flex items-center gap-1"><X size={15} /> Back to guides</button>
      <h1 className="font-display font-extrabold text-2xl text-navy mb-6">{guide.id ? "Edit guide" : "New guide"}</h1>

      <div className="max-w-2xl space-y-5 bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <Field label="Title"><input data-testid="guide-title" value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} placeholder="The Ultimate Guide to..." /></Field>
        <Field label="Description"><textarea data-testid="guide-description" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} /></Field>
        <Field label="Order"><input data-testid="guide-order" type="number" value={form.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} className={`${inputCls} w-28`} /></Field>

        <div>
          <ImageUpload label="Cover image (optional)" value={form.image || ""} onChange={(v) => set("image", v)} altValue={form.image_alt} onAltChange={(v) => set("image_alt", v)} hint="Shown on the Insights page instead of the document icon." />
        </div>

        <div>
          <label className="block text-sm font-semibold text-navy mb-1.5">Document (PDF / Word / Excel)</label>
          <p className="text-xs text-slate-400 mb-3">Leave empty to show "Coming Soon". Upload a document to make the guide downloadable (max 25MB).</p>
          {form.file_url ? (
            <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-3">
              <FileText size={18} className="text-orange" />
              <a href={mediaUrl(form.file_url)} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-navy hover:text-orange flex-1 truncate">View uploaded document</a>
              <button onClick={() => set("file_url", "")} className="text-xs text-red-500 hover:underline">Remove</button>
            </div>
          ) : (
            <>
              <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,application/pdf" onChange={handleFile} className="hidden" />
              <button data-testid="guide-upload-doc" type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 border border-slate-300 rounded-full px-5 py-2.5 text-sm font-semibold text-navy hover:bg-slate-50">
                {uploading ? <><Loader2 size={15} className="animate-spin" /> Uploading...</> : <><Upload size={15} /> Upload document</>}
              </button>
            </>
          )}
        </div>

        <div className="border-t border-slate-100 pt-5">
          <label className="flex items-center gap-2 text-sm font-semibold text-navy">
            <input type="checkbox" data-testid="guide-followup-enabled" checked={!!form.followup_enabled} onChange={(e) => set("followup_enabled", e.target.checked)} /> Send a follow-up email 3 days after download
          </label>
          <p className="text-xs text-slate-400 mt-1 mb-4">A friendly "did this help?" email tailored to this guide, sent automatically 3 days after someone downloads it. Only new downloads (going forward) receive it.</p>
          <div className="space-y-4">
            <Field label="Follow-up subject"><input data-testid="guide-followup-subject" value={form.followup_subject || ""} onChange={(e) => set("followup_subject", e.target.value)} className={inputCls} placeholder="How did you get on with the guide?" /></Field>
            <Field label="Follow-up message"><textarea data-testid="guide-followup-body" rows={4} value={form.followup_body || ""} onChange={(e) => set("followup_body", e.target.value)} className={inputCls} placeholder="I hope the guide was useful..." /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Button label"><input data-testid="guide-followup-cta-label" value={form.followup_cta_label || ""} onChange={(e) => set("followup_cta_label", e.target.value)} className={inputCls} placeholder="Book a call" /></Field>
              <Field label="Button link"><input data-testid="guide-followup-cta-link" value={form.followup_cta_link || ""} onChange={(e) => set("followup_cta_link", e.target.value)} className={inputCls} placeholder="/#contact" /></Field>
            </div>
          </div>
        </div>

        <button data-testid="guide-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark disabled:opacity-60 text-white font-bold px-6 py-2.5 rounded-full transition-colors">
          <Save size={16} /> {saving ? "Saving..." : "Save guide"}
        </button>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40";
const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-semibold text-navy mb-1.5">{label}</label>
    {children}
  </div>
);
